import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { X, Users as UsersIcon, User as UserIcon } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import Modal from 'components/ui/Modal';
import { Avatar } from 'components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui';
import { cn } from 'lib/utils';
import * as CollectionsModule from 'modules/collections';
import * as TeamsModule from 'modules/teams';
import * as Organization from 'modules/organization';
import { useAuthContext } from 'modules/auth';
import config from 'config';
import type { ShareAccess, ShareTarget } from 'modules/collections/types';

interface Props {
  open: boolean;
  onClose: () => void;
  collectionId: string;
  currentShares: ShareTarget[];
  onChanged: () => void;
}

interface OrgMemberRow {
  userId?: string;
  user?: { id?: string; name?: string; email?: string; avatarUrl?: string };
}

export const ShareCollectionModal: React.FC<Props> = ({
  open,
  onClose,
  collectionId,
  currentShares,
  onChanged,
}) => {
  const { t } = useTranslation();
  const { state: authState } = useAuthContext();
  // useOrgStore is not populated in the customer app; reading it here left
  // orgId undefined and the members query stayed disabled (empty People tab).
  const orgId =
    localStorage.getItem(config.api.active_org_key) ||
    authState.organization?.id ||
    undefined;
  const [tab, setTab] = useState<'user' | 'team'>('user');
  const [search, setSearch] = useState('');
  const [access, setAccess] = useState<ShareAccess>('view');

  const share = CollectionsModule.Hooks.useShare();
  const unshare = CollectionsModule.Hooks.useUnshare();
  const { teams } = TeamsModule.Hooks.useList();

  const membersQuery = useQuery<OrgMemberRow[]>({
    queryKey: ['org-members', orgId],
    enabled: open && Boolean(orgId),
    queryFn: async () => {
      const res = await Organization.Api.GetMembers(orgId as string);
      const data = (res.data as any)?.data ?? res.data ?? [];
      return Array.isArray(data) ? data : [];
    },
  });

  const sharedUserIds = useMemo(
    () => new Set(currentShares.filter((s) => s.type === 'user').map((s) => s.targetId)),
    [currentShares],
  );
  const sharedTeamIds = useMemo(
    () => new Set(currentShares.filter((s) => s.type === 'team').map((s) => s.targetId)),
    [currentShares],
  );

  const userCandidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (membersQuery.data ?? [])
      .filter((m) => {
        const uid = m.userId ?? m.user?.id;
        return uid && !sharedUserIds.has(uid);
      })
      .filter((m) =>
        !q ||
        (m.user?.name ?? '').toLowerCase().includes(q) ||
        (m.user?.email ?? '').toLowerCase().includes(q),
      );
  }, [membersQuery.data, sharedUserIds, search]);

  const teamCandidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (teams ?? [])
      .filter((tm: any) => !sharedTeamIds.has(tm.id))
      .filter((tm: any) => !q || (tm.name ?? '').toLowerCase().includes(q));
  }, [teams, sharedTeamIds, search]);

  const doShare = async (type: 'user' | 'team', targetId?: string, accessLevel: ShareAccess = access) => {
    if (!targetId) return;
    try {
      await share.mutateAsync({ collectionId, type, targetId, access: accessLevel });
      onChanged();
    } catch {
      /* hook toast */
    }
  };

  const doUnshare = async (type: 'user' | 'team', targetId: string) => {
    try {
      await unshare.mutateAsync({ collectionId, type, targetId });
      onChanged();
    } catch {
      /* hook toast */
    }
  };

  return (
    <Modal isOpen={open} onClose={onClose} title={t('collections.share_collection')}>
      <div className="space-y-4">
        {/* Current shares */}
        {currentShares.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('collections.shared_with_label')}</p>
            <div className="flex flex-wrap gap-2">
              {currentShares.map((s) => (
                <span key={`${s.type}-${s.targetId}`} className="inline-flex items-center gap-1.5 rounded-md bg-muted px-3 py-1 text-sm">
                  {s.type === 'team' ? <UsersIcon className="h-3.5 w-3.5" /> : <UserIcon className="h-3.5 w-3.5" />}
                  {s.target?.name ?? (s.type === 'team' ? t('collections.team') : t('collections.user'))}
                  <button
                    type="button"
                    onClick={() => doShare(s.type, s.targetId, s.access === 'edit' ? 'view' : 'edit')}
                    disabled={share.isPending}
                    className="rounded bg-background/70 px-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
                    title={t('collections.access')}
                  >
                    {s.access === 'edit' ? t('collections.can_edit') : t('collections.can_view')}
                  </button>
                  <button type="button" onClick={() => doUnshare(s.type, s.targetId)} className="text-muted-foreground hover:text-destructive">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2">
          {(['user', 'team'] as const).map((tp) => (
            <button key={tp} type="button" onClick={() => setTab(tp)} className={cn('flex-1 whitespace-nowrap rounded-lg py-2 text-sm font-medium capitalize', tab === tp ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/70')}>
              {tp === 'user' ? t('collections.people') : t('collections.teams')}
            </button>
          ))}
        </div>

        <div className="flex gap-2">
          <Input
            className="flex-1"
            placeholder={tab === 'user' ? t('collections.search_people') : t('collections.search_teams')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Select value={access} onValueChange={(v) => setAccess(v as ShareAccess)}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="view">{t('collections.can_view')}</SelectItem>
              <SelectItem value="edit">{t('collections.can_edit')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="max-h-[45vh] overflow-y-auto divide-y">
          {tab === 'user' ? (
            membersQuery.isLoading ? (
              <p className="py-6 text-center text-sm text-muted-foreground">{t('loading')}</p>
            ) : userCandidates.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">{t('collections.no_people')}</p>
            ) : (
              userCandidates.map((m) => {
                const uid = m.userId ?? m.user?.id;
                return (
                  <div key={uid} className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-3">
                      <Avatar src={m.user?.avatarUrl} name={m.user?.name} size="sm" />
                      <div>
                        <p className="text-sm font-medium">{m.user?.name || t('collections.unknown_user', 'Unknown')}</p>
                        <p className="text-xs text-muted-foreground">{m.user?.email}</p>
                      </div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => doShare('user', uid)} disabled={share.isPending}>
                      {t('collections.share')}
                    </Button>
                  </div>
                );
              })
            )
          ) : teamCandidates.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">{t('collections.no_teams')}</p>
          ) : (
            teamCandidates.map((tm: any) => (
              <div key={tm.id} className="flex items-center justify-between py-2">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                    <UsersIcon className="h-4 w-4" aria-hidden />
                  </div>
                  <p className="text-sm font-medium">{tm.name}</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => doShare('team', tm.id)} disabled={share.isPending}>
                  {t('collections.share')}
                </Button>
              </div>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ShareCollectionModal;
